import { getDisambiguatedFileIds } from "@/utils/file"
import { isCatalogFileName } from "@/lib/catalog/catalog-file-name"

export async function hasPermission(
	handle: FileSystemHandle,
	mode: "read" | "readwrite" = "read",
	queryOnly = false,
) {
	type PermissionCapableHandle = FileSystemHandle & {
		queryPermission?: (
			descriptor?: FileSystemHandlePermissionDescriptor,
		) => Promise<PermissionState>
		requestPermission?: (
			descriptor?: FileSystemHandlePermissionDescriptor,
		) => Promise<PermissionState>
	}

	const permissionHandle = handle as PermissionCapableHandle
	const queryPermission = permissionHandle.queryPermission?.bind(permissionHandle)
	const requestPermission = permissionHandle.requestPermission?.bind(permissionHandle)
	const descriptor: FileSystemHandlePermissionDescriptor = { mode }

	if (!queryPermission && !requestPermission) {
		// SAFARI_WORKAROUND: Safari misses the permission helpers, so optimistically proceed and
		// rely on later file ops to surface real permission issues.
		return true
	}

	if (queryPermission) {
		const queryPermissionResult = await queryPermission(descriptor)
		if (queryOnly) {
			return queryPermissionResult === "granted"
		}
		if (queryPermissionResult === "granted") {
			return true
		}
	}

	if (!requestPermission) {
		return false
	}

	const requestPermissionResult = await requestPermission(descriptor)
	return requestPermissionResult === "granted"
}

export interface PathInfo {
	path: string
	photoCount: number
}

export interface FilesInfo {
	files: File[]
	fileIds: string[]
	filePaths: Record<string, string>
	filePathList: string[]
	paths: PathInfo[]
	sideCars: Record<string, File>
	nestedCatalogPath: string | null
}

export async function getFiles(
	directory: FileSystemDirectoryHandle,
	parentPath: string | null = null,
	options: {
		allowedExtensions?: string[]
	} = {},
	rootName: string = directory.name,
): Promise<FilesInfo> {
	const path: string = parentPath ? `${parentPath}/${directory.name}` : directory.name

	let paths: PathInfo[] = []
	let files: File[] = []
	let filePathList: string[] = []
	let filePaths: Record<string, string> = {}
	let sideCars: Record<string, File> = {}
	let nestedCatalogPath: string | null = null

	let photoCount = 0

	const getValidFile = async (handle: FileSystemFileHandle) => {
		if (handle.name.startsWith(".")) {
			return null
		}

		if (options.allowedExtensions && !hasExtension(handle, options.allowedExtensions)) {
			return null
		}

		const file = await handle.getFile()

		// Filter out empty files (Dropbox?)
		if (!file.size) {
			return null
		}

		return file
	}

	for await (const handle of directory.values()) {
		if (handle.kind == "directory") {
			const result = await getFiles(handle, path, options, rootName)
			files = [...files, ...result.files]
			filePathList = [...filePathList, ...result.filePathList]
			sideCars = { ...sideCars, ...result.sideCars }
			nestedCatalogPath = nestedCatalogPath ?? result.nestedCatalogPath

			if (Object.keys(result.files).length > 0) {
				paths = [...paths, ...result.paths]
			}
		} else if (isCatalogFileName(handle.name) && parentPath !== null) {
			// Block importing a parent folder that contains another project nested inside it.
			if (!nestedCatalogPath) {
				const prefix = `${rootName}/`
				nestedCatalogPath = path.startsWith(prefix) ? path.slice(prefix.length) : path
			}
		} else if (hasExtension(handle, [".xmp"])) {
			//This is a sidecar file
			const sideCar = await handle.getFile()
			const fileName = handle.name.split(".").at(0)
			if (fileName) {
				sideCars[fileName] = sideCar
			}
		} else {
			const file = await getValidFile(handle)

			if (file) {
				files.push(file)
				filePathList.push(path)
				photoCount++
			}
		}
	}
	paths = [{ path, photoCount: photoCount }, ...paths]

	const fileIds = getDisambiguatedFileIds(files, filePathList)
	filePaths = Object.fromEntries(fileIds.map((id, index) => [id, filePathList[index] ?? path]))

	return { files, fileIds, filePaths, filePathList, paths, sideCars, nestedCatalogPath }
}

export function extension(file: FileSystemFileHandle | File | string) {
	const name = file instanceof File || file instanceof FileSystemFileHandle ? file.name : file
	const parts = name.split(".")
	return parts.length > 1 ? parts.pop() : ""
}

export function hasExtension(file: FileSystemFileHandle | File, extensions: string[]) {
	return extensions.some((extension) => {
		const dotted = extension.startsWith(".") ? extension : `.${extension}`
		return file.name.toLowerCase().endsWith(dotted.toLowerCase())
	})
}

export async function loadFileData(source: FileSystemFileHandle | File | string) {
	if (source instanceof FileSystemFileHandle) {
		source = await source.getFile()
	}

	const buffer =
		source instanceof File
			? await source.arrayBuffer()
			: await fetch(source).then((res) => res.arrayBuffer())

	return new Uint8Array(buffer)
}

export async function loadFile(source: FileSystemFileHandle | File | string): Promise<File> {
	if (source instanceof FileSystemHandle) {
		return source.getFile()
	}

	if (source instanceof File) {
		return source
	}

	const blob = await fetch(source).then((res) => res.blob())
	return new File([await blob.arrayBuffer()], "local")
}
